import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Loader2, Flame, GraduationCap, ExternalLink } from 'lucide-react';
import { api, formatDate } from '../lib/api';
import { useLanguage } from '../i18n';
import { ResultVideoPlayer } from '../components/result/ResultVideoPlayer';
import { ScoreCard } from '../components/result/ScoreCard';
import { TelemetryPanel } from '../components/result/TelemetryPanel';
import { MetricAccordions } from '../components/result/MetricAccordions';
import { BallControlCard, StrengthsCard, SuggestionsCard } from '../components/result/InsightCards';
import { ShareDialog } from '../components/result/ShareDialog';
import { ReviewDialog } from '../components/result/ReviewDialog';
import { NotFoundState, LoadingState, AnalyzingState, FailedState } from '../components/result/ResultStates';

export default function Result() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = searchParams.get('new') === '1';
  const { t, lang } = useLanguage();

  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [variant, setVariant] = useState('original');
  const pollRef = useRef(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [postId, setPostId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/analyses/${id}`);
      setData(d);
      setPostId(d.post_id);
      return d;
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else console.error('Failed to load analysis:', err);
      return null;
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      const d = await fetchData();
      if (d && d.video.status === 'analyzing') {
        pollRef.current = setInterval(async () => {
          const nd = await fetchData();
          if (nd && nd.video.status !== 'analyzing') clearInterval(pollRef.current);
        }, 2500);
      }
      if (d?.video?.overlay_path || d?.video?.pose_metrics) setVariant(d.video.overlay_path ? 'overlay' : 'original');
    })();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [id, fetchData]);

  if (notFound) return <NotFoundState onBack={() => navigate('/dashboard')} />;
  if (!data) return <LoadingState />;

  const { video, report } = data;

  if (video.status === 'analyzing') return <AnalyzingState step={video.step} onBack={() => navigate('/dashboard')} />;
  if (video.status === 'failed') return <FailedState error={video.error} onRetry={() => navigate('/analyze')} />;

  const lowConfidence = report && report.confidence < 0.4;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="result-page">
      <div className="flex items-center justify-between pt-1">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-white/55 hover:text-white transition-colors" data-testid="result-back-button">
          <ArrowLeft size={15} /> {t('backToDashboard')}
        </button>
        <span className="font-mono text-[11px] text-white/40">{formatDate(video.created_at, lang)}</span>
      </div>

      <ResultVideoPlayer analysisId={id} hasOverlay={Boolean(video.overlay_path)} variant={variant} onVariantChange={setVariant} />

      {report ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <ScoreCard report={report} isNew={isNew} />
            <TelemetryPanel bio={report.biomechanics} ball={report.ball_control} />
          </div>

          {/* Actions: share + coach review (only for owner) */}
          {data.is_mine && (
            <div className="flex gap-2.5">
              {postId ? (
                <button onClick={() => navigate('/pitch')} data-testid="result-view-post-button" className="ss-btn-glass flex-1 h-11 text-sm font-medium flex items-center justify-center gap-2">
                  <Flame size={15} className="text-white/70" /> {t('viewPost')} <ExternalLink size={12} />
                </button>
              ) : (
                <button onClick={() => setShareOpen(true)} data-testid="result-share-button" className="ss-btn-primary flex-1 h-11 text-sm font-bold font-display flex items-center justify-center gap-2">
                  <Flame size={15} /> {t('shareOnPitch')}
                </button>
              )}
              <button onClick={() => setReviewOpen(true)} data-testid="result-request-review-button" className="ss-btn-glass flex-1 h-11 text-sm font-medium flex items-center justify-center gap-2">
                <GraduationCap size={15} className="text-white/70" /> {t('requestCoachReview')}
              </button>
            </div>
          )}

          {lowConfidence && (
            <div className="flex items-start gap-2.5 text-xs text-[#E9C46A] bg-[rgba(233,196,106,0.07)] border border-[rgba(233,196,106,0.25)] rounded-[14px] px-3.5 py-3" data-testid="result-low-confidence-note">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" /> {t('lowConfidenceNote')}
            </div>
          )}

          <MetricAccordions report={report} />
          <BallControlCard ball={report.ball_control} />
          <StrengthsCard report={report} />
          <SuggestionsCard report={report} />
        </>
      ) : (
        <div className="ss-card p-8 flex items-center justify-center gap-2 text-white/55 text-sm">
          <Loader2 size={15} className="animate-spin" /> {t('processing')}
        </div>
      )}

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} analysisId={id} onShared={setPostId} />
      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} analysisId={id} />
    </div>
  );
}
